import _ from 'lodash';
import * as yup from 'yup';

export default yup.object().shape({
    company: yup.object().shape({
        type: yup.string().required(),
        company_name: yup.string().when('type', {
            is: (type) => type === 'ltd' || type === 'llp',
            then: () => yup.string().required(),
            otherwise: () =>
                yup
                    .string()
                    .transform(() => null)
                    .required()
        }),
        company_number: yup.string().when('type', {
            is: (type) => type === 'ltd' || type === 'llp',
            then: () => yup.string().required(),
            otherwise: () =>
                yup
                    .string()
                    .transform(() => null)
                    .required()
        }),
        trading_as: yup.string().when('type', {
            is: (type) => type === 'st' || type === 'op' || type === 'other',
            then: () => yup.string(),
            otherwise: () =>
                yup
                    .string()
                    .transform(() => null)
                    .required()
        }),
        annual_business_profit: yup.number().when('type', {
            is: (type) => type === 'st' || type === 'op' || type === 'other',
            then: () => yup.number().required(),
            otherwise: () =>
                yup
                    .number()
                    .transform(() => null)
                    .required()
        }),
        providers: yup.array().min(1),
        annual_turnover: yup.number().required(),
        website: yup.string().required(),
        industry: yup.string().when('type', {
            is: (type) => type === 'st' || type === 'op' || type === 'other',
            then: () => yup.string().required(),
            otherwise: () =>
                yup
                    .string()
                    .transform(() => null)
                    .required()
        }),
        vat_registered_status: yup.boolean().required(),
        online_sales_percentage: yup.number().required(),
        registered: yup.string().required()
    }),
    people: yup
        .array()
        .when('company.type', {
            is: (type) => type === 'st' || type === 'op' || type === 'other',
            then: () =>
                yup.array().of(
                    yup.object().shape({
                        first_name: yup.string().required(),
                        last_name: yup.string().required(),
                        title: yup.string().required(),
                        date_of_birth: yup.string().required(),
                        email: yup.string().email().required(),
                        roles: yup.array(),
                        marketing_emails: yup.boolean().required(),
                        privacy_policy_agreement_date: yup.date().required(),
                        phone: yup
                            .array()
                            .of(
                                yup
                                    .object()
                                    .shape({
                                        type: yup.string().required(),
                                        number: yup.string().required()
                                    })
                                    .transform((phone) =>
                                        !_.isEmpty(phone.number) ? phone : null
                                    )
                                    .required()
                            )
                            .transform((phones) => {
                                const numbers = _.filter(
                                    phones,
                                    (phone) => phone.number
                                );
                                return numbers.length ? phones : null;
                            })
                            .required(),
                        how_did_you_find_us: yup.object().shape({
                            answer: yup.string(),
                            details: yup.string()
                        }),
                        addresses: yup.array().when('roles', {
                            is: (roles) => _.includes(roles, 'applicant'),
                            then: () =>
                                yup
                                    .array()
                                    .of(
                                        yup.lazy((value) => {
                                            const isMainAddress =
                                                !value['extra_address'];
                                            if (isMainAddress) {
                                                return yup.object().shape({
                                                    street_line_1: yup
                                                        .string()
                                                        .when('auto', {
                                                            is: true,
                                                            then: () =>
                                                                yup.string(),
                                                            otherwise: () =>
                                                                yup
                                                                    .string()
                                                                    .required()
                                                        }),
                                                    town: yup
                                                        .string()
                                                        .required(),
                                                    postcode: yup
                                                        .string()
                                                        .required(),
                                                    country: yup
                                                        .string()
                                                        .required(),
                                                    residential_status: yup
                                                        .string()
                                                        .required(),
                                                    monthly_expenses: yup
                                                        .number()
                                                        .required(),
                                                    auto: yup
                                                        .boolean()
                                                        .required()
                                                });
                                            } else {
                                                return yup.object().shape({
                                                    street_line_1: yup.string(),
                                                    town: yup.string(),
                                                    postcode: yup.string(),
                                                    country: yup.string(),
                                                    residential_status:
                                                        yup.string(),
                                                    monthly_expenses:
                                                        yup.number(),
                                                    auto: yup.boolean()
                                                });
                                            }
                                        })
                                    )
                                    .transform((addresses) => {
                                        const postcodes = _.filter(
                                            addresses,
                                            (address) => address.postcode
                                        );
                                        return postcodes.length
                                            ? addresses
                                            : null;
                                    })
                                    .required(),
                            otherwise: () =>
                                yup
                                    .array()
                                    .of(
                                        yup.object().shape({
                                            street_line_1: yup
                                                .string()
                                                .when('auto', {
                                                    is: true,
                                                    then: () => yup.string(),
                                                    otherwise: () =>
                                                        yup.string().required()
                                                }),
                                            town: yup.string().required(),
                                            postcode: yup.string().required(),
                                            country: yup.string().required(),
                                            auto: yup.boolean().required()
                                        })
                                    )
                                    .transform((addresses) => {
                                        const postcodes = _.filter(
                                            addresses,
                                            (address) => address.postcode
                                        );
                                        return postcodes.length
                                            ? addresses
                                            : null;
                                    })
                                    .required()
                        }),
                        monthly_non_business_income: yup
                            .number()
                            .when('roles', {
                                is: (roles) => _.includes(roles, 'applicant'),
                                then: () => yup.number(),
                                otherwise: () =>
                                    yup
                                        .number()
                                        .transform(() => null)
                                        .required()
                            }),
                        other_monthly_expenses: yup.number().when('roles', {
                            is: (roles) => _.includes(roles, 'applicant'),
                            then: () => yup.number(),
                            otherwise: () =>
                                yup
                                    .number()
                                    .transform(() => null)
                                    .required()
                        }),
                        number_of_dependents: yup
                            .number()
                            .positive()
                            .integer()
                            .when('roles', {
                                is: (roles) => _.includes(roles, 'applicant'),
                                then: () => yup.number().positive().integer(),
                                otherwise: () =>
                                    yup
                                        .number()
                                        .positive()
                                        .integer()
                                        .transform(() => null)
                            }),
                        monthly_other_household_income: yup
                            .number()
                            .when(['roles', 'number_of_dependents'], {
                                is: (roles, number_of_dependents) => {
                                    return (
                                        _.includes(roles, 'applicant') &&
                                        number_of_dependents > 0
                                    );
                                },
                                then: () => yup.number().required(),
                                otherwise: () => yup.number()
                            })
                    })
                ),
            otherwise: () =>
                yup.array().of(
                    yup.object().shape({
                        first_name: yup.string().required(),
                        last_name: yup.string().required(),
                        title: yup.string().required(),
                        email: yup.string().email().required(),
                        date_of_birth: yup.string().required(),
                        roles: yup.array(),
                        marketing_emails: yup.boolean().required(),
                        privacy_policy_agreement_date: yup.date().required(),
                        how_did_you_find_us: yup.object().shape({
                            answer: yup.string(),
                            details: yup.string()
                        }),
                        phone: yup
                            .array()
                            .of(
                                yup
                                    .object()
                                    .shape({
                                        type: yup.string().required(),
                                        number: yup.string().required()
                                    })
                                    .transform((phone) =>
                                        !_.isEmpty(phone.number) ? phone : null
                                    )
                                    .required()
                            )
                            .transform((phones) => {
                                const numbers = _.filter(
                                    phones,
                                    (phone) => phone.number
                                );
                                return numbers.length ? phones : null;
                            })
                            .required(),
                        addresses: yup
                            .array()
                            .of(
                                yup.lazy((value) => {
                                    const isMainAddress =
                                        !value['extra_address'];
                                    if (isMainAddress) {
                                        return yup
                                            .object()
                                            .shape({
                                                street_line_1: yup
                                                    .string()
                                                    .when('auto', {
                                                        is: true,
                                                        then: () =>
                                                            yup.string(),
                                                        otherwise: () =>
                                                            yup
                                                                .string()
                                                                .required()
                                                    }),
                                                town: yup.string().required(),
                                                postcode: yup
                                                    .string()
                                                    .required(),
                                                country: yup
                                                    .string()
                                                    .required(),
                                                residential_status: yup
                                                    .string()
                                                    .required(),
                                                monthly_expenses: yup
                                                    .number()
                                                    .transform(() => null)
                                                    .required(),
                                                auto: yup.boolean().required()
                                            })
                                            .required();
                                    } else {
                                        return yup
                                            .object()
                                            .shape({
                                                street_line_1: yup.string(),
                                                town: yup.string(),
                                                postcode: yup.string(),
                                                country: yup.string(),
                                                residential_status:
                                                    yup.string(),
                                                monthly_expenses: yup
                                                    .number()
                                                    .transform(() => null),
                                                auto: yup.boolean()
                                            })
                                            .required();
                                    }
                                })
                            )
                            .transform((addresses) => {
                                const postcodes = _.filter(
                                    addresses,
                                    (address) => address.postcode
                                );
                                return postcodes.length ? addresses : null;
                            })
                            .required(),
                        monthly_non_business_income: yup
                            .number()
                            .transform(() => null)
                            .required(),
                        number_of_dependents: yup
                            .number()
                            .transform(() => null)
                            .required(),
                        monthly_other_household_income: yup
                            .number()
                            .transform(() => null)
                            .required(),
                        other_monthly_expenses: yup
                            .number()
                            .transform(() => null)
                            .required()
                    })
                )
        })
        .required(),
    loan_request: yup
        .object()
        .shape({
            urgency: yup.string().required(),
            amount: yup.number().required(),
            purpose: yup.string().when('urgency', {
                is: 'future_use',
                then: () => yup.string().transform(() => null),
                otherwise: () => yup.string().required()
            }),
            detailed_reason: yup.string().when('purpose', {
                is: (purpose) => purpose === 'other',
                then: () => yup.string().required(),
                otherwise: () =>
                    yup
                        .string()
                        .transform(() => null)
                        .required()
            })
        })
        .transform((obj) => {
            const v = _.values(obj).filter((v) => !_.isEmpty(v) || v > 0);
            return v.length ? obj : null;
        })
        .required()
});
